import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import HeroPhoto from "@/components/hero/HeroPhoto";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, GlassCard } from "@/components/common/ui";
import {
  PHOTO_RECEPTION, PHOTO_HOUSEKEEPING, PHOTO_MANAGER, PHOTO_KITCHEN,
  PHOTO_TECHNICAL, PHOTO_MARKETING, PHOTO_RESTAURANT,
} from "@/data/photos";
import {
  CheckCircle,
  ArrowRight,
  UsersFour,
  Users,
  UserGear,
  Books,
  Sun,
  Coffee,
  ForkKnife,
  DeviceMobile,
  ChartBar,
  MoonStars,
  Chat,
  ShieldCheck,
  Buildings,
  House,
  Storefront,
  Waves,
  BuildingApartment,
  Envelope,
  Globe,
  Info,
} from "@phosphor-icons/react";

const EASE = [0.16, 1, 0.3, 1];

/** Small status pill (Beschikbaar / Pilot / In ontwikkeling / Gepland). */
function StatusPill({ label }) {
  const key = (label || "").toLowerCase();
  const tone =
    key.includes("beschik") || key.includes("available")
      ? "border-emerald-400/40 text-emerald-300/90 bg-emerald-400/5"
      : key.includes("pilot")
      ? "border-bht-gold/50 text-bht-gold bg-bht-gold/5"
      : "border-white/15 text-white/60 bg-white/[0.02]";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>
      <span className="h-1 w-1 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

/** Small reusable check-list. */
function CheckList({ items, testIdPrefix }) {
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-white/75" data-testid={`${testIdPrefix}-${i}`}>
          <CheckCircle size={16} weight="regular" className="mt-1 shrink-0 text-bht-gold" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

const DAY_ICONS = [Sun, Coffee, ForkKnife, DeviceMobile, ChartBar, MoonStars];
const DAY_PHOTOS = [PHOTO_HOUSEKEEPING, PHOTO_RECEPTION, PHOTO_KITCHEN, PHOTO_RESTAURANT, PHOTO_MANAGER, PHOTO_TECHNICAL];
const SEGMENT_ICONS = [House, Storefront, BuildingApartment, Waves, Buildings];

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";

  const hpAnchors = t("hp.hero.anchors", { returnObjects: true }) || [];
  const problems = t("hp.problems.list", { returnObjects: true }) || [];
  const centralBenefits = t("hp.central.benefits", { returnObjects: true }) || [];
  const centralMock = t("hp.central.mock", { returnObjects: true }) || {};
  const valuesOwner = t("hp.values.owner", { returnObjects: true }) || {};
  const valuesTeam = t("hp.values.team", { returnObjects: true }) || {};
  const valuesGuest = t("hp.values.guest", { returnObjects: true }) || {};
  const day = t("hp.day.timeline", { returnObjects: true }) || [];
  const managerQualities = t("hp.manager.qualities", { returnObjects: true }) || [];
  const kbTopics = t("hp.kb.topics", { returnObjects: true }) || [];
  const kbQuestions = t("hp.kb.questions", { returnObjects: true }) || [];
  const collabBenefits = t("hp.collab.benefits", { returnObjects: true }) || [];
  const emailLinks = t("hp.email.links", { returnObjects: true }) || [];
  const channelsList = t("hp.channels.list", { returnObjects: true }) || [];
  const channelsBenefits = t("hp.channels.benefits", { returnObjects: true }) || [];
  const insightBenefits = t("hp.insight.benefits", { returnObjects: true }) || [];
  const pmsBenefits = t("hp.pms.benefits", { returnObjects: true }) || [];
  const pmsList = t("hp.pms.list", { returnObjects: true }) || [];
  const segments = t("hp.segments.list", { returnObjects: true }) || [];
  const originQuestions = t("hp.origin.questions", { returnObjects: true }) || [];
  const originBenefits = t("hp.origin.benefits", { returnObjects: true }) || [];
  const trustBenefits = t("hp.trust.benefits", { returnObjects: true }) || [];
  const finalList = t("hp.final.list", { returnObjects: true }) || [];

  return (
    <>
      <SEO
        title={lang === "nl" ? "Het digitale hart van úw hotel." : "The digital heart of your hotel."}
        description={t("hp.hero.sub")}
        path=""
      />

      {/* ============ 1 · HERO ============ */}
      <section
        className="relative isolate flex min-h-[92svh] items-center overflow-hidden"
        data-testid="home-hero"
      >
        <HeroPhoto />
        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-12 gap-8 px-6 pb-28 pt-24 lg:px-10">
          <div className="col-span-12 lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <Eyebrow>{t("hp.hero.eyebrow")}</Eyebrow>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.1, ease: EASE }}
              className="mt-8 font-serif text-[44px] leading-[1.02] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[76px]"
              data-testid="home-hero-h1"
            >
              <span className="block">{t("hp.hero.h1_a")}</span>
              <span className="block italic text-bht-gold/90">{t("hp.hero.h1_b")}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.25, ease: EASE }}
              className="mt-8 max-w-2xl text-lg leading-relaxed text-white/80 md:text-xl"
              data-testid="home-hero-sub"
            >
              {t("hp.hero.sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <GoldButton to={`/${lang}/book-demo`} testId="home-hero-cta-demo">
                {t("hp.hero.cta_primary")}
              </GoldButton>
              <GhostButton to={`/${lang}/bht-one`} testId="home-hero-cta-explore">
                {t("hp.hero.cta_secondary")} →
              </GhostButton>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: EASE }}
              className="mt-14 flex flex-wrap gap-x-6 gap-y-2"
              data-testid="home-hero-anchors"
            >
              {hpAnchors.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-white/60">
                  <span className="h-1 w-1 rounded-full bg-bht-gold" />
                  <span className="font-mono uppercase tracking-widest text-[10px]">{a}</span>
                </li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* ============ 2 · PROBLEMS ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-problems">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading
            eyebrow={t("hp.problems.eyebrow")}
            title={t("hp.problems.h2")}
            sub={t("hp.problems.intro")}
          />
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 5) * 0.04, ease: EASE }}
                className="group flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 transition-colors hover:border-bht-gold/25"
                data-testid={`home-problem-${i}`}
              >
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/10 font-mono text-[10px] text-white/50 group-hover:border-bht-gold/60 group-hover:text-bht-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-[15px] leading-relaxed text-white/80">{p}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 rounded-2xl border border-bht-gold/25 bg-bht-gold/[0.04] p-6 lg:p-8" data-testid="home-problems-example">
            <div className="eyebrow mb-3">{lang === "nl" ? "Een moment dat u vast herkent" : "A moment you might recognise"}</div>
            <p className="font-serif text-xl italic leading-relaxed text-white/90 md:text-2xl">
              &ldquo;{t("hp.problems.example")}&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* ============ 3 · CENTRAL WORKSPACE ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-central">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("hp.central.eyebrow")}
              title={t("hp.central.h2")}
              sub={t("hp.central.intro")}
            />
            <div className="mt-10">
              <CheckList items={centralBenefits} testIdPrefix="home-central-benefit" />
            </div>
            <div className="mt-10">
              <GhostButton to={`/${lang}/bht-one`} testId="home-central-cta">
                {t("hp.central.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-7">
            {/* Manager foto — de echte BHT ONE werkomgeving in context */}
            <figure
              className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-black/45 shadow-[0_30px_120px_-40px_rgba(200,160,69,0.35)]"
              data-testid="home-central-mock"
            >
              <img
                src={PHOTO_MANAGER}
                alt={lang === "nl"
                  ? "Manager bekijkt de centrale werkomgeving van BHT One in zijn kantoor."
                  : "A manager reviews the BHT One central workspace in their office."}
                className="block h-auto w-full"
                loading="lazy"
                decoding="async"
              />
              {/* Bottom caption overlay */}
              <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-6 lg:p-8">
                <div className="eyebrow text-[10px]">{centralMock.eyebrow}</div>
                <div className="mt-1 font-serif text-lg leading-tight text-white/95">{centralMock.title}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    lang === "nl" ? "Receptie" : "Reception",
                    "Housekeeping",
                    lang === "nl" ? "Restaurant" : "Restaurant",
                    lang === "nl" ? "Onderhoud" : "Maintenance",
                    "Marketing",
                    lang === "nl" ? "Kennisbank" : "Knowledge base",
                  ].map((c) => (
                    <span key={c} className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs text-white/85 backdrop-blur">
                      {c}
                    </span>
                  ))}
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ============ 4 · THREE VALUE LAYERS ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-values">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading
            eyebrow={t("hp.values.eyebrow")}
            title={t("hp.values.h2")}
            sub={t("hp.values.intro")}
          />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {[
              { icon: UserGear, data: valuesOwner, tid: "owner" },
              { icon: UsersFour, data: valuesTeam, tid: "team" },
              { icon: Users, data: valuesGuest, tid: "guest" },
            ].map(({ icon: Icon, data, tid }, i) => (
              <motion.div
                key={tid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8"
                data-testid={`home-values-${tid}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-bht-gold/35 bg-bht-navy2">
                  <Icon size={20} weight="thin" className="text-bht-gold" />
                </div>
                <div className="mt-6 font-serif text-2xl leading-tight">{data.title}</div>
                <ul className="mt-6 space-y-2.5">
                  {(data.items || []).map((it, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/70">
                      <span className="mt-2 h-1 w-2.5 shrink-0 bg-bht-gold/70" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5 · A DAY WITH BHT ONE ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-day">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading
            eyebrow={t("hp.day.eyebrow")}
            title={t("hp.day.h2")}
            sub={t("hp.day.intro")}
          />
          <p className="mt-6 max-w-2xl text-sm text-white/50" data-testid="home-day-note">
            <Info size={14} weight="regular" className="mr-1 inline-block align-[-2px] text-bht-gold" />
            {t("hp.day.note")}
          </p>

          <ol className="relative mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {day.map((d, i) => {
              const Icon = DAY_ICONS[i] || Sun;
              const photo = DAY_PHOTOS[i];
              return (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease: EASE }}
                  className="group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-black/40"
                  data-testid={`home-day-${i}`}
                >
                  {photo && (
                    <div className="relative h-40 w-full overflow-hidden">
                      <img
                        src={photo}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute left-4 top-4">
                        <StatusPill label={d.status} />
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-bht-gold/35 bg-bht-navy2">
                        <Icon size={16} weight="thin" className="text-bht-gold" />
                      </div>
                      <div className="font-mono text-sm tracking-widest text-bht-gold">{d.time}</div>
                    </div>
                    <p className="mt-5 text-[15px] leading-relaxed text-white/80">{d.title}</p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============ 6 · DIGITAL HOTEL MANAGER ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-manager">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("hp.manager.eyebrow")}
              title={t("hp.manager.h2")}
              sub={t("hp.manager.intro")}
            />
            <div className="mt-10">
              <GhostButton to={`/${lang}/modules/ai-copilot`} testId="home-manager-cta">
                {t("hp.manager.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              {managerQualities.map((q, i) => (
                <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={`home-manager-quality-${i}`}>
                  <div className="font-serif text-lg leading-tight">{q.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{q.body}</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-bht-gold/25 bg-black/40 p-6" data-testid="home-manager-example">
              <div className="eyebrow mb-3">{lang === "nl" ? "Vraag van een receptionist" : "Question from a receptionist"}</div>
              <div className="rounded-lg bg-white/[0.03] px-4 py-3 text-sm text-white/85">&ldquo;{t("hp.manager.example_q")}&rdquo;</div>
              <div className="mt-3 rounded-lg border border-bht-gold/30 bg-bht-gold/5 px-4 py-3 text-sm leading-relaxed text-bht-gold/90">
                {t("hp.manager.example_a")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 7 · KNOWLEDGE BASE ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-kb">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("hp.kb.eyebrow")}
              title={t("hp.kb.h2")}
              sub={t("hp.kb.intro")}
            />
            <div className="mt-10">
              <GhostButton to={`/${lang}/knowledge`} testId="home-kb-cta">
                {t("hp.kb.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/[0.06] bg-black/45 p-6 lg:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-bht-gold/35 bg-bht-navy2">
                  <Books size={18} weight="thin" className="text-bht-gold" />
                </div>
                <div>
                  <div className="eyebrow text-[10px]">{lang === "nl" ? "Kennisbank · Hoofdcategorieën" : "Knowledge base · Main topics"}</div>
                  <div className="font-serif text-lg leading-tight">
                    {lang === "nl" ? "Wat er in het geheugen van uw hotel zit" : "What lives in your hotel's memory"}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {kbTopics.map((topic, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2.5 text-center text-xs text-white/75"
                    data-testid={`home-kb-topic-${i}`}
                  >
                    {topic}
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-white/[0.06] pt-5">
                <div className="eyebrow mb-3">{lang === "nl" ? "Wat medewerkers vragen" : "What the team asks"}</div>
                <ul className="space-y-2">
                  {kbQuestions.map((q, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 rounded-lg bg-white/[0.02] px-4 py-2.5 text-sm text-white/80"
                      data-testid={`home-kb-question-${i}`}
                    >
                      <Chat size={14} weight="thin" className="shrink-0 text-bht-gold" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 8 · COLLABORATION ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-collab">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow={t("hp.collab.eyebrow")}
              title={t("hp.collab.h2")}
              sub={t("hp.collab.intro")}
            />
            <div className="mt-10">
              <CheckList items={collabBenefits} testIdPrefix="home-collab-benefit" />
            </div>
            <div className="mt-10">
              <GhostButton to={`/${lang}/modules`} testId="home-collab-cta">
                {t("hp.collab.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-6">
            {/* Photo diptych — housekeeping + technical service; two departments, one context */}
            <div className="grid grid-cols-2 gap-3" data-testid="home-collab-photos">
              <figure className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
                <img
                  src={PHOTO_HOUSEKEEPING}
                  alt={lang === "nl"
                    ? "Housekeeping-medewerker rondt kamer 214 af met BHT One naast zich."
                    : "A housekeeping employee finishes room 214 with BHT One beside them."}
                  className="h-full w-full object-cover aspect-[4/5]"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">Housekeeping</div>
                  <div className="mt-1 text-xs text-white/90">
                    {lang === "nl" ? "Kamer klaar · gast verwacht" : "Room ready · guest expected"}
                  </div>
                </figcaption>
              </figure>
              <figure className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
                <img
                  src={PHOTO_TECHNICAL}
                  alt={lang === "nl"
                    ? "Technisch medewerker lost een melding op — de status verschijnt direct in de werkomgeving."
                    : "A maintenance colleague resolves a ticket — the status appears directly in the workspace."}
                  className="h-full w-full object-cover aspect-[4/5]"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">
                    {lang === "nl" ? "Technische dienst" : "Maintenance"}
                  </div>
                  <div className="mt-1 text-xs text-white/90">
                    {lang === "nl" ? "Melding opgevolgd · zichtbaar voor receptie" : "Ticket resolved · visible to reception"}
                  </div>
                </figcaption>
              </figure>
            </div>

            <div className="mt-4 rounded-2xl border border-bht-gold/25 bg-bht-gold/[0.04] p-6" data-testid="home-collab-example">
              <div className="eyebrow mb-3">{lang === "nl" ? "Een voorbeeld" : "One example"}</div>
              <p className="font-serif text-lg italic leading-relaxed text-white/90 md:text-xl">
                &ldquo;{t("hp.collab.example")}&rdquo;
              </p>
            </div>

            {/* Business email teaser */}
            <div className="mt-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid="home-email-block">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-black/30">
                    <Envelope size={16} weight="thin" className="text-bht-gold" />
                  </div>
                  <div>
                    <div className="eyebrow text-[10px]">{t("hp.email.eyebrow")}</div>
                    <div className="font-serif text-base leading-tight">{t("hp.email.h2")}</div>
                  </div>
                </div>
                <StatusPill label={t("hp.status.development")} />
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/65">{t("hp.email.intro")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {emailLinks.map((l, i) => (
                  <span key={i} className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1 text-xs text-white/70">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 9 · WEBSITE & GUEST APP ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-channels">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow={t("hp.channels.eyebrow")}
              title={t("hp.channels.h2")}
              sub={t("hp.channels.intro")}
            />
            <div className="mt-10">
              <CheckList items={channelsBenefits} testIdPrefix="home-channels-benefit" />
            </div>
            <div className="mt-10">
              <GhostButton to={`/${lang}/modules/website`} testId="home-channels-cta">
                {t("hp.channels.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-6">
            {/* Marketing foto — de marketing-medewerker beheert website, gastenapp en campagnes vanuit BHT One */}
            <figure
              className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-black/40"
              data-testid="home-channels-mock"
            >
              <img
                src={PHOTO_MARKETING}
                alt={lang === "nl"
                  ? "Marketing-medewerker beheert website, gastenapp en campagnes vanuit één BHT One-scherm."
                  : "A marketing colleague manages website, guest app and campaigns from a single BHT One screen."}
                className="block h-auto w-full"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-6 lg:p-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-bht-gold/40 bg-black/50 backdrop-blur">
                    <Globe size={16} weight="thin" className="text-bht-gold" />
                  </div>
                  <div>
                    <div className="eyebrow text-[10px]">{lang === "nl" ? "Content · centraal beheerd" : "Content · centrally managed"}</div>
                    <div className="font-serif text-base leading-tight text-white/95">
                      {lang === "nl" ? "Uw website + gastenapp + campagnes" : "Your website + guest app + campaigns"}
                    </div>
                  </div>
                </div>
              </figcaption>
            </figure>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {channelsList.map((c, i) => (
                <div key={i} className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2.5 text-center text-xs text-white/75" data-testid={`home-channels-item-${i}`}>
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 10 · INSIGHT / INTELLIGENCE ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-insight">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow={t("hp.insight.eyebrow")}
              title={t("hp.insight.h2")}
              sub={t("hp.insight.intro")}
            />
            <div className="mt-10">
              <CheckList items={insightBenefits} testIdPrefix="home-insight-benefit" />
            </div>
            <div className="mt-10">
              <GhostButton to={`/${lang}/modules/analytics`} testId="home-insight-cta">
                {t("hp.insight.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-white/[0.06] bg-black/45 p-6 lg:p-8" data-testid="home-insight-mock">
              <div className="eyebrow mb-4 text-[10px]">
                {lang === "nl" ? "Uw ochtend · 07:00" : "Your morning · 07:00"}
              </div>
              <ul className="space-y-3">
                {(lang === "nl"
                  ? [
                      "Vandaag verwachten wij 14 aankomsten. Twee daarvan komen later dan 22:00.",
                      "Uw restaurant heeft vanavond 42 couverts — dat is 15% boven een normale maandag.",
                      "Kamer 12 heeft een openstaande onderhoudsmelding sinds gisterochtend.",
                      "Uw wijnmarge zakte deze week 3 punten — wij leggen uit waarom onderaan het scherm.",
                    ]
                  : [
                      "You are expecting 14 arrivals today. Two of them arrive after 22:00.",
                      "Your restaurant tonight holds 42 covers — 15% above a normal Monday.",
                      "Room 12 has an open maintenance ticket since yesterday morning.",
                      "Your wine margin dropped 3 points this week — we explain why at the bottom of this screen.",
                    ]
                ).map((row, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-lg border border-white/[0.05] bg-white/[0.02] px-4 py-3 text-sm text-white/80">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-bht-gold" />
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-white/[0.06] pt-5 text-sm italic leading-relaxed text-white/60">
                <span className="not-italic font-mono uppercase tracking-widest text-[10px] text-bht-gold">
                  {lang === "nl" ? "Principe · " : "Principle · "}
                </span>
                {t("hp.insight.principle")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 11 · WORKS ALONGSIDE YOUR PMS ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-pms">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow={t("hp.pms.eyebrow")}
                title={t("hp.pms.h2")}
                sub={t("hp.pms.intro")}
              />
              <div className="mt-10">
                <CheckList items={pmsBenefits} testIdPrefix="home-pms-benefit" />
              </div>
              <div className="mt-8 rounded-xl border border-bht-gold/25 bg-bht-gold/[0.04] p-5" data-testid="home-pms-example">
                <p className="text-sm italic leading-relaxed text-white/80">&ldquo;{t("hp.pms.example")}&rdquo;</p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6 lg:p-8">
                <div className="eyebrow mb-6 text-[10px]">{lang === "nl" ? "Werkt naast:" : "Works alongside:"}</div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {pmsList.map((name, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-4 text-center font-serif text-base text-white/85 transition-colors hover:border-bht-gold/30"
                      data-testid={`home-pms-item-${i}`}
                    >
                      {name}
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs leading-relaxed text-white/45">{t("hp.pms.list_note")}</p>
              </div>
              <div className="mt-6">
                <GhostButton to={`/${lang}/platform`} testId="home-pms-cta">
                  {t("hp.pms.cta")} →
                </GhostButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 12 · SEGMENTS ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-segments">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow={t("hp.segments.eyebrow")}
                title={t("hp.segments.h2")}
                sub={t("hp.segments.intro")}
              />
            </div>
            <div className="lg:col-span-5">
              <figure className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
                <img
                  src={PHOTO_RESTAURANT}
                  alt={lang === "nl"
                    ? "Een gastvrouw verwelkomt terugkerende gasten in het hotelrestaurant."
                    : "A hostess welcomes returning guests in the hotel restaurant."}
                  className="block h-full w-full object-cover aspect-[16/9]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 p-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">
                    {lang === "nl" ? "Ongeacht formaat · dezelfde gastvrijheid" : "Whatever the size · the same hospitality"}
                  </div>
                </figcaption>
              </figure>
            </div>
          </div>
          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {segments.map((s, i) => {
              const Icon = SEGMENT_ICONS[i] || House;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                  className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/25"
                  data-testid={`home-segment-${i}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-bht-gold/35 bg-bht-navy2">
                    <Icon size={18} weight="thin" className="text-bht-gold" />
                  </div>
                  <div className="mt-5 font-serif text-lg leading-tight">{s.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{s.body}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10">
            <GhostButton to={`/${lang}/solutions`} testId="home-segments-cta">
              {t("hp.segments.cta")} →
            </GhostButton>
          </div>
        </div>
      </section>

      {/* ============ 13 · ORIGIN ============ */}
      <section className="relative border-y border-white/[0.06] bg-bht-navy2/30 py-28 lg:py-36" data-testid="home-origin">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("hp.origin.eyebrow")}
              title={t("hp.origin.h2")}
              sub={t("hp.origin.intro")}
            />
            <div className="mt-10">
              <GhostButton to={`/${lang}/company`} testId="home-origin-cta">
                {t("hp.origin.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-8">
            <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6 lg:p-8" data-testid="home-origin-questions">
              <div className="eyebrow mb-4">{lang === "nl" ? "Vragen die BHT One heeft doen ontstaan" : "The questions that started BHT One"}</div>
              <ul className="space-y-3">
                {originQuestions.map((q, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-white/80">
                    <span className="mt-1 font-mono text-[10px] text-bht-gold">?</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {originBenefits.map((b, i) => (
                <div key={i} className="rounded-2xl border border-bht-gold/20 bg-bht-gold/[0.03] p-5 text-sm leading-relaxed text-white/85" data-testid={`home-origin-benefit-${i}`}>
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 14 · TRUST ============ */}
      <section className="relative overflow-hidden py-28 lg:py-36" data-testid="home-trust">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("hp.trust.eyebrow")}
              title={t("hp.trust.h2")}
              sub={t("hp.trust.intro")}
            />
            <div className="mt-10">
              <GhostButton to={`/${lang}/trust`} testId="home-trust-cta">
                {t("hp.trust.cta")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6 lg:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-bht-gold/35 bg-bht-navy2">
                  <ShieldCheck size={18} weight="thin" className="text-bht-gold" />
                </div>
                <div>
                  <div className="eyebrow text-[10px]">{lang === "nl" ? "Zorgvuldig by design" : "Careful by design"}</div>
                  <div className="font-serif text-lg leading-tight">
                    {lang === "nl" ? "Wat wij aantoonbaar doen" : "What we can demonstrate"}
                  </div>
                </div>
              </div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {trustBenefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02] px-4 py-3 text-sm leading-relaxed text-white/80" data-testid={`home-trust-benefit-${i}`}>
                    <CheckCircle size={14} weight="regular" className="mt-1 shrink-0 text-bht-gold" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 15 · FINAL CTA ============ */}
      <section className="relative overflow-hidden border-t border-white/[0.06] bg-bht-navy2/40 py-28 lg:py-36" data-testid="home-final">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow={t("hp.final.eyebrow")}
              title={t("hp.final.h2")}
              sub={t("hp.final.intro")}
            />
            <div className="mt-10 flex flex-wrap gap-4">
              <GoldButton to={`/${lang}/book-demo`} testId="home-final-cta-primary">
                {t("hp.final.cta_primary")}
              </GoldButton>
              <GhostButton to={`/${lang}/contact`} testId="home-final-cta-secondary">
                {t("hp.final.cta_secondary")} →
              </GhostButton>
            </div>
          </div>
          <div className="lg:col-span-6">
            <GlassCard testId="home-final-list" hover={false}>
              <div className="eyebrow mb-6">{lang === "nl" ? "Wat u kunt verwachten" : "What to expect"}</div>
              <ul className="space-y-4">
                {finalList.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-white/80" data-testid={`home-final-item-${i}`}>
                    <ArrowRight size={16} weight="regular" className="mt-1 shrink-0 text-bht-gold" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>
        </div>
      </section>
    </>
  );
}
